import React, { useEffect, useState } from 'react';

const GoogleCallback = () => {
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Adding contact to Google...');

  useEffect(() => {
    const addContactToGoogle = async () => {
      try {
        // Extract access token from URL hash
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);
        const accessToken = params.get('access_token');

        if (!accessToken) {
          throw new Error('No access token received from Google');
        }

        // Get contact data from localStorage
        const contactDataStr = localStorage.getItem('pendingGoogleContact');
        console.log('Contact data from localStorage:', contactDataStr);
        
        if (!contactDataStr) {
          throw new Error('No contact data found. Please try again from the bio page.');
        }

        const contactData = JSON.parse(contactDataStr);
        console.log('Parsed contact data:', contactData);
        
        // Clean up
        localStorage.removeItem('pendingGoogleContact');

        // Build contact resource for Google People API
        const contact = {
          names: [{
            givenName: contactData.givenName || contactData.name?.split(' ')[0] || '',
            familyName: contactData.familyName || contactData.name?.split(' ').slice(1).join(' ') || ''
          }],
          emailAddresses: contactData.email ? [{
            value: contactData.email,
            type: 'work'
          }] : [],
          phoneNumbers: [
            ...(contactData.phone ? [{
              value: contactData.phone,
              type: 'work'
            }] : []),
            ...(contactData.phone2 ? [{
              value: contactData.phone2,
              type: 'mobile'
            }] : [])
          ],
          organizations: contactData.company || contactData.title ? [{
            name: contactData.company || '',
            title: contactData.title || '',
            type: 'work'
          }] : [],
          urls: [
            ...(contactData.website || (contactData.websiteName && contactData.tld) ? [{
              value: contactData.website || `https://${contactData.websiteName}${contactData.tld}`,
              type: 'work'
            }] : []),
            ...(contactData.linkedin ? [{
              value: `https://linkedin.com/in/${contactData.linkedin}`,
              type: 'profile'
            }] : []),
            ...(contactData.twitter ? [{
              value: `https://twitter.com/${contactData.twitter}`,
              type: 'profile'
            }] : []),
            ...(contactData.github ? [{
              value: `https://github.com/${contactData.github}`,
              type: 'profile'
            }] : []),
            ...(contactData.instagram ? [{
              value: `https://instagram.com/${contactData.instagram}`,
              type: 'profile'
            }] : []),
            ...(contactData.facebook ? [{
              value: `https://facebook.com/${contactData.facebook}`,
              type: 'profile'
            }] : []),
            ...(contactData.youtube ? [{
              value: contactData.youtube.startsWith('@') ? `https://youtube.com/${contactData.youtube}` : `https://youtube.com/channel/${contactData.youtube}`,
              type: 'profile'
            }] : [])
          ],
          addresses: contactData.locationAddress || contactData.location ? [{
            formattedValue: contactData.locationAddress || contactData.location,
            type: 'work'
          }] : [],
          biographies: contactData.bio ? [{
            value: contactData.bio,
            contentType: 'TEXT_PLAIN'
          }] : []
        };

        // Add contact using Google People API
        const response = await fetch('https://people.googleapis.com/v1/people:createContact', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(contact)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error?.message || 'Failed to create contact');
        }

        const result = await response.json();
        console.log('Contact created:', result);

        setStatus('success');
        setMessage('Contact successfully added to Google Contacts!');

        // Notify parent window
        if (window.opener) {
          window.opener.postMessage({
            type: 'GOOGLE_AUTH_SUCCESS',
            contact: result
          }, '*');
        }

        // Close popup after 2 seconds
        setTimeout(() => {
          window.close();
        }, 2000);

      } catch (error) {
        console.error('Error adding contact:', error);
        setStatus('error');
        setMessage(error.message || 'Failed to add contact to Google');

        // Notify parent window of error
        if (window.opener) {
          window.opener.postMessage({
            type: 'GOOGLE_AUTH_ERROR',
            error: error.message
          }, '*');
        }

        // Close popup after 3 seconds
        setTimeout(() => {
          window.close();
        }, 3000);
      }
    };

    addContactToGoogle();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-amber-50">
      <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full mx-4">
        <div className="text-center">
          {status === 'processing' && (
            <>
              <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Adding Contact</h2>
              <p className="text-gray-600">{message}</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-green-600 mb-2">Success!</h2>
              <p className="text-gray-600">{message}</p>
              <p className="text-sm text-gray-500 mt-2">This window will close automatically...</p>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-red-600 mb-2">Error</h2>
              <p className="text-gray-600">{message}</p>
              <p className="text-sm text-gray-500 mt-2">This window will close automatically...</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleCallback;
